import {
  type RiskGapConfidenceRating,
  RiskGapModel,
  riskGapConfidenceRatings,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { searchAllRecords, searchFirstPartyRecords } from '@ctw/shared/api/fqs-rest/search-helpers';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import {
  QUERY_KEY_PATIENT_RISK_GAPS,
  QUERY_KEY_PATIENT_RISK_GAPS_INDICATOR,
} from '@ctw/shared/utils/query-keys';
import { SYSTEM_URLS } from '@zeus-health/types';

/**
 * FQS `_tag` token meaning "owned by this builder". MeasureReport permits `_tag`
 * alongside `upid` (fqs internal/rest/scope.go validParameters) and FQS applies it
 * during the UPID scan (internal/rest/server.go, filterCriteria -> FilterBy), so other
 * builders' reports are never returned. Preferred over filtering the response
 * client-side: the `_count` limit then applies to *matching* records, so a patient with
 * many builders' reports can't push this builder's gaps out of the page. (GAP-755)
 *
 * Do not URL-encode — searchRecords runs params through URLSearchParams.
 */
const builderOwnerTag = (builderId: string) =>
  `${SYSTEM_URLS.TAGS.OWNER_BUILDER_ID}|builder/${builderId}`;

/*
Gets patient risk gaps with UI exclusions applied.
*/
export const usePatientRiskGapsWithUIExclusions = () => {
  const builderConfig = useBuilderConfig();
  const crossBuilderLookup = useFeatureFlag('ctw-cross-builder-upid-lookup');

  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_RISK_GAPS,
    queryFn: async ({ requestContext, ctwFetch, patient }) => {
      if (!patient.UPID) {
        return [];
      }

      // Under cross-builder lookup the viewed patient may be owned by another builder
      // (selectBestPatient falls back past the caller's builder when it has no active
      // copy). Scope to that patient's owner so their gaps resolve — APP-5042's intent,
      // which only takes effect now that this value drives the _tag filter. Safe because
      // _tag narrows: FQS still authorizes every record independently. (GAP-755)
      const scopedBuilderId = crossBuilderLookup ? patient.builderId : requestContext.builderId;

      const records = await searchFirstPartyRecords(
        ctwFetch,
        {
          env: requestContext.env,
          builderId: scopedBuilderId,
          authToken: requestContext.authToken,
        },
        'MeasureReport',
        {
          upid: patient.UPID ?? '',
          _count: '500',
          _tag: builderOwnerTag(scopedBuilderId),
        },
      );

      const riskGaps = records
        .filter(
          (report) =>
            report.measure === SYSTEM_URLS.MEASURE.CMS_V28 &&
            report.meta?.profile?.includes(SYSTEM_URLS.PROFILE.RISK_MEASURE_REPORT),
        )
        .map((report) => {
          return new RiskGapModel(report);
        });

      let filteredRiskGaps = riskGaps;

      // Filter out risk gaps with excluded HCC codes
      const excludedHccCodes = builderConfig.riskGaps?.uiExcludedHccCodes ?? [];
      if (excludedHccCodes.length > 0) {
        filteredRiskGaps = filteredRiskGaps.filter((riskGap) => {
          return !excludedHccCodes.includes(riskGap.hccCode);
        });
      }

      // Filter out risk gaps based on confidence level
      const confidenceLevelThreshold = builderConfig.riskGaps?.displayGapsWithConfidenceLevelsAbove;
      if (
        confidenceLevelThreshold &&
        riskGapConfidenceRatings.includes(confidenceLevelThreshold as RiskGapConfidenceRating)
      ) {
        const thresholdIndex = riskGapConfidenceRatings.indexOf(
          confidenceLevelThreshold as RiskGapConfidenceRating,
        );

        filteredRiskGaps = filteredRiskGaps.filter((riskGap) => {
          if (!riskGap.confidenceRating) {
            return true;
          }
          const riskGapIndex = riskGapConfidenceRatings.indexOf(riskGap.confidenceRating);
          return riskGapIndex > thresholdIndex;
        });
      }
      return filteredRiskGaps;
    },
  });
};

export const useHasRiskGapData = () => {
  const crossBuilderLookup = useFeatureFlag('ctw-cross-builder-upid-lookup');
  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_RISK_GAPS_INDICATOR,
    queryFn: async ({ requestContext, ctwFetch, patient }) => {
      if (!patient.UPID) {
        return false;
      }
      const scopedBuilderId = crossBuilderLookup ? patient.builderId : requestContext.builderId;
      // _count is 500 (not 1): FQS scopes the response to this builder via _tag, but the
      // measure/profile check below is still client-side (MeasureReport has no _profile
      // param), so a single-record page could return only a RAF report and wrongly
      // report "no risk gaps". 500 is fqs MaxPageSize (dbretriever/query.go) — the most
      // one request can ask for, and headroom for a builder's own RAF and payer-gap
      // reports, which carry the same owner tag and so occupy slots here. (GAP-755)
      const records = await searchFirstPartyRecords(
        ctwFetch,
        {
          env: requestContext.env,
          builderId: scopedBuilderId,
          authToken: requestContext.authToken,
        },
        'MeasureReport',
        {
          upid: patient.UPID,
          _count: '500',
          _tag: builderOwnerTag(scopedBuilderId),
        },
      );
      return records.some(
        (r) =>
          r.measure === SYSTEM_URLS.MEASURE.CMS_V28 &&
          r.meta?.profile?.includes(SYSTEM_URLS.PROFILE.RISK_MEASURE_REPORT),
      );
    },
  });
};

interface GetPatientRiskGapArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  ids: Array<string>;
}

export const getRiskGapsByIds = async ({
  ctwFetch,
  requestContext,
  ids,
}: GetPatientRiskGapArgs) => {
  return (
    await searchAllRecords(ctwFetch, requestContext, 'MeasureReport', {
      _id: ids.join(','),
    })
  ).map((riskGap) => new RiskGapModel(riskGap));
};
